import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'

import { resolvePerencanaanReviewDiff } from 'app/KinerjaShared/utils/perencanaan'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import { PerencanaanReviewDiff } from 'app/KinerjaShared/components/PerencanaanReviewDiff'

import type { PerencanaanItemReviewDiff } from 'types/kinerja'
import type {
  KinerjaPraktikPembelajaranContentIndikator,
  KinerjaPraktikPembelajaranDimensi,
} from 'types/kinerja'

export type PerencanaanPraktikKinerjaItemProps = {
  originalId: number
  reviewId: number
  showReview: boolean
  contents: Array<KinerjaPraktikPembelajaranDimensi>
  orderNumber: number
}

export function PerencanaanPraktikKinerjaItem({
  originalId,
  reviewId,
  showReview,
  contents,
  orderNumber,
}: PerencanaanPraktikKinerjaItemProps) {
  // Content as map for constant time lookup
  const contentsMap: Record<
    number,
    KinerjaPraktikPembelajaranContentIndikator
  > = useMemo(() => {
    return contents.reduce((acc, content) => {
      content.indikator.forEach((indikator) => {
        acc[indikator.id] = indikator
      })
      return acc
    }, {} as Record<number, KinerjaPraktikPembelajaranContentIndikator>)
  }, [contents])

  // Resolved items
  const resolvedItem: PerencanaanItemReviewDiff<string> = useMemo(() => {
    let item: PerencanaanItemReviewDiff<number> = null

    if (showReview) {
      item = resolvePerencanaanReviewDiff([originalId], [reviewId])[0]
    } else {
      item = {
        isChanged: false,
        original: originalId,
        review: null,
      }
    }

    // Map to content
    const mappedItem: PerencanaanItemReviewDiff<string> = {
      original: null,
      review: null,
      isChanged: item.isChanged,
    }

    if (item.original) {
      mappedItem.original = contentsMap[item.original]?.nama
    }

    if (item.review) {
      mappedItem.review = contentsMap[item.review]?.nama
    }

    return mappedItem
  }, [originalId, reviewId, contentsMap, showReview])

  const shouldShowReview = showReview && resolvedItem.isChanged

  return (
    <IndikatorPerencanaanPanel
      variant="static"
      orderNumber={orderNumber}
      upperContent={
        shouldShowReview && (
          <PerencanaanReviewDiff
            originalLabel="Perencanaan Guru"
            reviewLabel="Penyesuaian dari Kepala Sekolah"
            originalContent={
              <IndikatorPerencanaanPanel
                variant="static"
                orderNumber={orderNumber}
              >
                <Text>
                  Terlaksananya praktik pembelajaran melalui observasi praktik
                  kinerja yang disepakati bersama Kepala Sekolah yang berfokus
                  pada&nbsp;
                  <strong>{resolvedItem.original}</strong>
                </Text>
              </IndikatorPerencanaanPanel>
            }
            reviewContent={
              <IndikatorPerencanaanPanel
                variant="static"
                orderNumber={orderNumber}
              >
                <Text>
                  Terlaksananya praktik pembelajaran melalui observasi praktik
                  kinerja yang disepakati bersama Kepala Sekolah yang berfokus
                  pada&nbsp;
                  <strong>{resolvedItem.review}</strong>
                </Text>
              </IndikatorPerencanaanPanel>
            }
          />
        )
      }
    >
      <Text>
        Terlaksananya praktik pembelajaran melalui observasi praktik kinerja
        yang disepakati bersama Kepala Sekolah yang berfokus pada&nbsp;
        <strong>
          {shouldShowReview ? resolvedItem.review : resolvedItem.original}
        </strong>
      </Text>
    </IndikatorPerencanaanPanel>
  )
}
